import { FC, MutableRefObject, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ActionTypeEnum,
  CustomerComponentDisplayModeEnum,
  CustomerResponseModel,
  HasFormIdModel,
  LookupItemModel,
  LookupTypeEnum,
  RequestActionModel,
  ResponseBaseModel,
  SupplierCustomerTypeEnum,
  ValidationErrorModel,
} from "../../models";
import {
  ButtonBox,
  CheckBox,
  ErrorValidationBox,
  LoadingBox,
  PrivilegesChecker,
  SelectBox,
  TextBox,
} from "../index";
import {
  getLabelName,
  getLookUpItemNumericValue,
  reValidateLocalStorageCachedData,
} from "../../utils";
import { Accordion, Card } from "react-bootstrap";
import {
  addCustomer,
  getCustomerInvoiceType,
} from "../../serviceBroker/customerApiServiceBroker";
import {
  getAllCountryIsoCode,
  getLookupByType,
} from "../../serviceBroker/lookupApiServiceBroker";
import {AddressRequestModel} from "../../models/customer/customerModel";

interface AddCustomerProps extends HasFormIdModel {
  request: CustomerResponseModel;
  type: SupplierCustomerTypeEnum;
  displayMode?: CustomerComponentDisplayModeEnum | null;
  onActionEvent: (o: RequestActionModel) => void;
}

export const AddBeneficiary: FC<AddCustomerProps> = ({
  request,
  type = SupplierCustomerTypeEnum.Customer,
  displayMode = CustomerComponentDisplayModeEnum.defaultMode,
  onActionEvent = () => {},
  formId,
}) => {
  //#region variables
  request.Cust_Type = request.Cust_Type == null ? type : request.Cust_Type;
  let initialValues: CustomerResponseModel = request;
  const addressInitialValues:AddressRequestModel={
    Address_GUID: "",
    CreatedBy: 0,
    CreationDate: new Date(),
    CustomerId: 0,
    FlatNumber: "",
    Governate: "",
    AdditionalNo:"",
    PostalCode:"",
    ID: 0,
    ModificationDate:  new Date(),
    ModifiedBy: 0,
    Name: "",
    RegionCity: "",
    Remarks: "",
    Row_State: 0,
    Street: "",
    VerifyOnUpdate: false,
    buildingNumber: "",
    rowState: 0
  };
  //#endregion
  //#region state
  const [cityList, setCityList] = useState<LookupItemModel[]>([]);
  const [paymentTypeList, setPaymentTypeList] = useState<LookupItemModel[]>([]);
  const [customerTypeList, setCustomerTypeList] = useState<LookupItemModel[]>(
    []
  );
  const [countryList, setCountryList] = useState<LookupItemModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrorModel[]
  >([]);
  const countrySelectBoxMultiselectRef = useRef<any>();
  const citySelectBoxMultiselectRef = useRef<any>();
  const [validationSchema] = useState(
    Yup.object({
      Name: Yup.string().required(getLabelName("name ar is missing")),
      Mail: Yup.string().email(getLabelName("validemail")),
    })
  );
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      await fillLookupDataPromiseAll(
        displayMode ?? CustomerComponentDisplayModeEnum.defaultMode
      );
    };
    fillData().then(() => {});
  }, []);
  //#endregion
  //#region functions
  const handleSubmit = async (
    request: CustomerResponseModel
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const response: ResponseBaseModel<CustomerResponseModel> =
        await addCustomer(request);

      if (
        response != null &&
        response.Errors != null &&
        response.Errors.length !== 0
      ) {
        setValidationErrors(response.Errors);
        setLoading(false);
        return false;
      } else {
        setValidationErrors([]);
        if (response?.Result) {
          await reValidateLocalCache(response.Result);
        }
        onActionEvent({
          id: 0,
          action: ActionTypeEnum.AddSuccess,
          request: response?.Result,
        });
        setLoading(false);
        return true;
      }
    } catch (err: any) {
      setLoading(false);
      const errors: ValidationErrorModel[] = [
        { MessageAr: err, MessageEn: err },
      ];
      setValidationErrors(errors);
      return false;
    }
  };
  const handleResetForm = async () => {
    await formik.setValues({ ...initialValues });
    await formik.setTouched({});
    formik.setErrors({});
    countrySelectBoxMultiselectRef.current &&
      countrySelectBoxMultiselectRef.current.clearValue();
    citySelectBoxMultiselectRef.current &&
      citySelectBoxMultiselectRef.current.clearValue();
    onActionEvent({ id: 0, action: ActionTypeEnum.Clear });
  };
  const fillLookupDataPromiseAll = async (
    mode = CustomerComponentDisplayModeEnum.defaultMode
  ) => {
    let cities: LookupItemModel[] | undefined,
      _paymentTypes: LookupItemModel[] | undefined,
      customerTypes: LookupItemModel[] | undefined,
      countries: LookupItemModel[] | undefined;
    if (mode === CustomerComponentDisplayModeEnum.defaultMode) {
      // Load all data in default mode
      [cities, _paymentTypes, customerTypes, countries] = await Promise.all([
        getLookupByType(LookupTypeEnum.Cities, true, false),
        getLookupByType(LookupTypeEnum.PaymentType, true, false),
        getCustomerInvoiceType(),
        getAllCountryIsoCode(),
      ]);
    } else {
      // Load only customer types in other modes
      [customerTypes] = await Promise.all([getCustomerInvoiceType()]);
    }
    // Conditionally set data only if default mode (all data)
    if (mode === CustomerComponentDisplayModeEnum.defaultMode) {
      setCityList(cities ?? []);
      setCountryList(countries ?? []);
      setPaymentTypeList(_paymentTypes ?? []);
    }
    // Always set customer types
    setCustomerTypeList(customerTypes);
  };
  const reValidateLocalCache = async (request: CustomerResponseModel) => {
    if (!request) return;

    const shouldValidateCustomer =
      type === SupplierCustomerTypeEnum.Customer ||
      request.ISCustomerAndSupplier;
    const shouldValidateSupplier =
      type === SupplierCustomerTypeEnum.Supplier ||
      request.ISCustomerAndSupplier;
    const tasks = [];
    if (shouldValidateCustomer) {
      tasks.push(reValidateLocalStorageCachedData(LookupTypeEnum.Customers));
    }
    if (shouldValidateSupplier) {
      tasks.push(reValidateLocalStorageCachedData(LookupTypeEnum.Supplier));
    }
    await Promise.all(tasks);
  };
  //#endregion
  //#region formik
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      values.Cust_Type = Number(type);
      const result = await handleSubmit(values);
      if (result) {
        resetForm();
      }
    },
  });
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      {<ErrorValidationBox errors={validationErrors} />}
      <form onSubmit={formik.handleSubmit}>
        <div
          // className="row row-cols-xxl-2 row-cols-xl-2 row-cols-lg-2 row-cols-md-1 row-cols-sm-1 g-md-4 g-sm-4"
          className="row row-cols-1 row-cols-md-3 g-4"
        >
          <div className="col-12">
            <TextBox
              labelName={getLabelName("Arabic Name")} //{t("lookup.nameAr")}
              inputName={"Name"}
              errorText={formik.errors.Name}
              inputValue={formik.values.Name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isMandatory={true}
            />
          </div>
          <div className="col-12">
            <TextBox
              labelName={getLabelName("English Name")}
              inputName={"Name_En"}
              errorText={formik.errors.Name_En}
              inputValue={formik.values.Name_En}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <div className="col-12">
            <TextBox
              labelName={getLabelName("Code")}
              inputName={"Code"}
              errorText={formik.errors.Code}
              inputValue={formik.values.Code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <div className="col-12">
            <TextBox
              labelName={getLabelName("E-Mail")}
              inputName={"Mail"}
              errorText={formik.errors.Mail}
              inputValue={formik.values.Mail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <>
            {/*<TextBox*/}
            {/*  labelName={getLabelName("Begin Bal")} //{t("lookup.nameAr")}*/}
            {/*  inputName={"BalanceOfPoint"}*/}
            {/*  errorText={formik.errors.BalanceOfPoint}*/}
            {/*  inputValue={formik.values.BalanceOfPoint}*/}
            {/*  onChange={formik.handleChange}*/}
            {/*  onBlur={formik.handleBlur}*/}
            {/*  type="number"*/}
            {/*/>*/}
            <div className="col-6">
              <TextBox
                labelName={getLabelName("Beg_AC_Bal")} //{t("lookup.nameAr")}
                inputName={"Beg_AC_Bal"}
                errorText={formik.errors.Beg_AC_Bal}
                inputValue={formik.values.Beg_AC_Bal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="number"
              />
            </div>
            <div className="col-6">
              <TextBox
                labelName={getLabelName("Max Debit")} //{t("lookup.nameAr")}
                inputName={"MaxDebit"}
                errorText={formik.errors.MaxDebit}
                inputValue={formik.values.MaxDebit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="number"
              />
            </div>
            <div className="col-12">
              <TextBox
                labelName={getLabelName("Phone")} //{t("lookup.nameAr")}
                inputName={"Mobile"}
                errorText={formik.errors.Mobile}
                inputValue={formik.values.Mobile}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="col-12">
              <TextBox
                labelName={getLabelName("ID")} //{t("lookup.nameAr")}
                inputName={"IDNumber"}
                errorText={formik.errors.IDNumber}
                inputValue={formik.values.IDNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </>
          <div className="col-12">
            <TextBox
              labelName={getLabelName("Tax Number")} //{t("lookup.nameAr")}
              inputName={"TaxNumber"}
              errorText={formik.errors.TaxNumber}
              inputValue={formik.values.TaxNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <div className="col-12">
            <SelectBox
              labelName={getLabelName(
                type == SupplierCustomerTypeEnum.Customer
                  ? "customer type"
                  : "supplier type"
              )}
              source={customerTypeList}
              isSingleSelect={true}
              onStatusChange={(e: any) => {
                formik.setFieldValue("CustomerType", e?.value ?? null);
                request.CustomerType = e?.value ?? null;
              }}
              selectedValues={
                request.CustomerType != null
                  ? [request.CustomerType?.toString()]
                  : []
              }
              multiselectRef={undefined}
              usePortalTarget={false}
            />
          </div>
          <div className="col-12">
            <SelectBox
              labelName={getLabelName("payment type")}
              source={paymentTypeList}
              isSingleSelect={true}
              onStatusChange={(e: any) => {
                formik.setFieldValue("PaymentTypeID", e?.value ?? null);
                request.PaymentTypeID = e?.value ?? null;
              }}
              selectedValues={
                request.CustomerType != null
                  ? [request.PaymentTypeID?.toString()]
                  : []
              }
              multiselectRef={undefined}
              usePortalTarget={false}
            />
          </div>
          <div className="col-12">
            <CheckBox
              onChange={(e: any) => {
                formik.values.ISCustomerAndSupplier = e.target.checked;
                formik.setFieldValue("ISCustomerAndSupplier", e.target.checked);
                request.ISCustomerAndSupplier = e.target.checked;
              }}
              id="ISCustomerAndSupplier"
              checked={request.ISCustomerAndSupplier}
              inputName="request.ISCustomerAndSupplier"
              labelName={getLabelName("ISCustomerAndSupplier")}
            />
          </div>
        </div>
        <div className="row mt-1 row-cols-1 row-cols-xxl-12 row-cols-xl-12 row-cols-lg-12 row-cols-md-12 row-cols-sm-12 g-md-12 g-sm-12">
          <TextBox
            labelName={getLabelName("Note")} //{t("lookup.nameAr")}
            inputName={"Notes"}
            errorText={formik.errors.Notes}
            inputValue={formik.values.Notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type={"textarea"}
          />
        </div>
        <Accordion defaultActiveKey="0" className="mt-4">
          <Accordion.Item eventKey="0">
            <Accordion.Header>{getLabelName("Detail")}</Accordion.Header>
            <Accordion.Body>
              <Card>
                <Card.Body>
                  <div className="row row-cols-1 row-cols-md-2 g-4">
                    <TextBox
                      labelName={getLabelName("Address")}
                      inputName={"Address"}
                      inputValue={formik.values.Address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <SelectBox
                      labelName={getLabelName("country")}
                      source={countryList}
                      isSingleSelect={true}
                      selectedValues={[
                        formik.values.address?.CountryCode == null
                          ? ""
                          : formik.values.address.CountryCode.toString(),
                      ]}
                      onStatusChange={(e: any) => {
                        const val: number | null = getLookUpItemNumericValue(e);
                        const code = val == null ? "" : val.toString();
                        formik.values.address ??= { ...addressInitialValues };
                        request.address ??= { ...addressInitialValues };
                        formik.values.address.CountryCode = code;
                        formik.setFieldValue("address.CountryCode", code);
                        request.address.CountryCode = code;
                      }}
                      //@ts-ignore
                      multiselectRef={(node: MutableRefObject<any>) =>
                        node
                          ? (countrySelectBoxMultiselectRef.current = node)
                          : null
                      }
                      usePortalTarget={false}
                    />

                    <SelectBox
                      labelName={getLabelName("City")}
                      source={cityList}
                      isSingleSelect={true}
                      key="Select City"
                      errorText={formik.errors.City_ID}
                      onStatusChange={(e: any) => {
                        const val: number | null = getLookUpItemNumericValue(e);
                        formik.values.City_ID = val;
                        formik.setFieldValue("City_ID", val);
                        request.City_ID = val;
                      }}
                      selectedValues={[
                        formik.values.City_ID == null
                          ? ""
                          : formik.values.City_ID.toString(),
                      ]}
                      //@ts-ignore
                      multiselectRef={(node: MutableRefObject<any>) =>
                        node
                          ? (citySelectBoxMultiselectRef.current = node)
                          : null
                      }
                      usePortalTarget={false}
                    />

                    <TextBox
                      labelName={getLabelName("Area")} //{t("lookup.nameAr")}
                      inputName={"address.RegionCity"}
                      inputValue={formik.values.address?.RegionCity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />

                    <TextBox
                      labelName={getLabelName("Street")} //{t("lookup.nameAr")}
                      inputName={"address.Street"}
                      inputValue={formik.values.address?.Street}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />

                    <TextBox
                      labelName={getLabelName("Building Number")}
                      inputName={"address.buildingNumber"}
                      inputValue={formik.values.address?.buildingNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <TextBox
                        labelName={getLabelName("Postal Code")}
                        inputName={"address.PostalCode"}
                        inputValue={formik.values.address?.PostalCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <TextBox
                        labelName={getLabelName("Additional No")}
                        inputName={"address.AdditionalNo"}
                        inputValue={formik.values.address?.AdditionalNo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                  </div>
                  <div className="row mt-4 row-cols-1 row-cols-xxl-12 row-cols-xl-12 row-cols-lg-12 row-cols-md-12 row-cols-sm-12 g-md-12 g-sm-12">
                    <TextBox
                      labelName={getLabelName("Remarks")} //{t("lookup.nameAr")}
                      inputName={"address.Remarks"}
                      inputValue={formik.values.address?.Remarks}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      type={"textarea"}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <div className="row">
          <div className="col-12 d-flex justify-content-end mt-4">
            <PrivilegesChecker
              formId={formId}
              action={request.ID ? "EnableUpdate" : "EnableSave"}
            >
              <ButtonBox
                iconType="content-save"
                type="submit"
                className="btn-gradient-primary"
              >
                {getLabelName(request.ID ? "Update" : "Save")}
              </ButtonBox>
            </PrivilegesChecker>
            {displayMode === CustomerComponentDisplayModeEnum.defaultMode && (
              <ButtonBox
                iconType="receipt"
                variant="danger"
                type="button"
                className="mx-3"
                onClick={async () => {
                  await handleResetForm();
                }}
              >
                {getLabelName("New")}
              </ButtonBox>
            )}
          </div>
        </div>
      </form>
    </>
  );
  //#endregion
};
